import { LlmModelFamily, LlmModelModality, LlmModelName, LlmParameterSupport, LlmProvider, LlmTemperatureSupport, LlmTopPSupport, } from './types.js';
const TEXT_ONLY = [LlmModelModality.Text];
const TEXT_AND_FILE = [LlmModelModality.Text, LlmModelModality.File];
/** The model accepts `temperature` and `top_p` together. */
const TEMPERATURE_AND_TOP_P = {
    temperature: LlmTemperatureSupport.Supported,
    topP: LlmTopPSupport.Supported,
};
/** The model accepts each of `temperature`/`top_p` but not simultaneously. */
const TEMPERATURE_OR_TOP_P = {
    temperature: LlmTemperatureSupport.ExcludesTopP,
    topP: LlmTopPSupport.ExcludesTemperature,
};
const SUPPORTED = LlmParameterSupport.Supported;
/**
 * Defaults for the parameter-support matrix. A model lists only the parameters
 * it supports; every other field becomes `Unsupported` (the safe default — we'd
 * rather under-use a capability than send a parameter the model rejects).
 */
const DEFAULT_SUPPORTED_PARAMETERS = {
    temperature: LlmTemperatureSupport.Unsupported,
    topP: LlmTopPSupport.Unsupported,
    tools: LlmParameterSupport.Unsupported,
    toolChoice: LlmParameterSupport.Unsupported,
    maxTokens: LlmParameterSupport.Unsupported,
    reasoning: LlmParameterSupport.Unsupported,
    includeReasoning: LlmParameterSupport.Unsupported,
    structuredOutputs: LlmParameterSupport.Unsupported,
    responseFormat: LlmParameterSupport.Unsupported,
    stop: LlmParameterSupport.Unsupported,
    frequencyPenalty: LlmParameterSupport.Unsupported,
    presencePenalty: LlmParameterSupport.Unsupported,
    seed: LlmParameterSupport.Unsupported,
};
/**
 * The model registry, authored in a terse input shape (optional/defaulted fields
 * omitted) and materialized with defaults applied (see `resolveModel`).
 *
 * Data provenance (see the package README): display names, ids, modalities and
 * the temperature/top_p matrix are migrated 1:1 from the former `web/vivaa-api`
 * config; intelligence scores are the Artificial Analysis Intelligence Index
 * v4.3.2, each model's highest-scoring variant (GPT-5 mini: the "high" variant),
 * retrieved 2026-09-24; lifecycle dates, successor families and 429/529 fallback chains
 * are ported from `backend/flow/api` (`modelLifecycle.ts`) and
 * `backend/predict/documents` (`models.py`); each historical `addedDate` was
 * recovered via `git log -S` over the original config (best-effort). Optional
 * fields not sourceable in-repo (context length, max output tokens, image
 * modality, the remaining parameter matrix) are left to their
 * `Unsupported`/unset defaults and filled in best-effort.
 */
const RAW_LLM_MODELS = {
    [LlmModelName.amazon_nova_pro]: {
        id: LlmModelName.amazon_nova_pro,
        name: 'Amazon Nova Pro',
        addedDate: '2024-12-23',
        provider: LlmProvider.Amazon,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 7,
        modelFamily: LlmModelFamily.AmazonNova,
    },
    [LlmModelName.claude_3_5_haiku]: {
        id: LlmModelName.claude_3_5_haiku,
        name: 'Claude 3.5 Haiku',
        addedDate: '2024-11-06',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2025-12-19',
            obsoleteDate: '2026-06-19',
            successorModelFamily: LlmModelFamily.ClaudeHaiku,
        },
        intelligenceScore: 9,
        modelFamily: LlmModelFamily.ClaudeHaiku,
    },
    [LlmModelName.claude_3_5_sonnet_v2]: {
        id: LlmModelName.claude_3_5_sonnet_v2,
        name: 'Claude 3.5 Sonnet V2',
        addedDate: '2024-12-23',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2025-08-20',
            obsoleteDate: '2026-02-19',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.ClaudeSonnet,
        },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.ClaudeSonnet,
    },
    [LlmModelName.claude_3_7_sonnet_v1]: {
        id: LlmModelName.claude_3_7_sonnet_v1,
        name: 'Claude 3.7 Sonnet',
        addedDate: '2025-03-01',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2025-10-28',
            obsoleteDate: '2026-04-28',
            successorModelFamily: LlmModelFamily.ClaudeSonnet,
        },
        intelligenceScore: 18,
        modelFamily: LlmModelFamily.ClaudeSonnet,
    },
    [LlmModelName.claude_opus_4]: {
        id: LlmModelName.claude_opus_4,
        name: 'Claude Opus 4',
        addedDate: '2025-05-27',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.ClaudeOpus,
        },
        intelligenceScore: 21,
        modelFamily: LlmModelFamily.ClaudeOpus,
        releaseVersion: 'anthropic.claude-opus-4-20250514-v1:0',
        resourceExhaustionFallbackChain: [LlmModelName.claude_opus_4_6],
    },
    [LlmModelName.claude_sonnet_4]: {
        id: LlmModelName.claude_sonnet_4,
        name: 'Claude Sonnet 4',
        addedDate: '2025-05-27',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.ClaudeSonnet,
        },
        intelligenceScore: 19,
        modelFamily: LlmModelFamily.ClaudeSonnet,
        releaseVersion: 'anthropic.claude-sonnet-4-20250514-v1:0',
        resourceExhaustionFallbackChain: [LlmModelName.claude_sonnet_4_5],
    },
    [LlmModelName.deepseek_r1]: {
        id: LlmModelName.deepseek_r1,
        name: 'DeepSeek R1',
        addedDate: '2025-02-10',
        provider: LlmProvider.DeepSeek,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-07-29',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 11,
        modelFamily: LlmModelFamily.DeepseekReasoning,
    },
    [LlmModelName.deepseek_v3]: {
        id: LlmModelName.deepseek_v3,
        name: 'DeepSeek V3',
        addedDate: '2025-03-07',
        provider: LlmProvider.DeepSeek,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-07-29',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.DeepseekChat,
    },
    [LlmModelName.gemini_1_5_pro]: {
        id: LlmModelName.gemini_1_5_pro,
        name: 'Gemini 1.5 Pro',
        addedDate: '2024-12-23',
        provider: LlmProvider.Google,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2025-09-24',
            successorModelFamily: LlmModelFamily.GeminiPro,
        },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.GeminiPro,
    },
    [LlmModelName.gemini_1_5_flash]: {
        id: LlmModelName.gemini_1_5_flash,
        name: 'Gemini 1.5 Flash',
        addedDate: '2024-12-23',
        provider: LlmProvider.Google,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2025-09-24',
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 7,
        modelFamily: LlmModelFamily.GeminiFlash,
    },
    [LlmModelName.gemini_2_0_flash_001]: {
        id: LlmModelName.gemini_2_0_flash_001,
        name: 'Gemini 2.0 Flash 001',
        addedDate: '2025-02-11',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-03-06',
            obsoleteDate: '2026-06-01',
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 9,
        modelFamily: LlmModelFamily.GeminiFlash,
        resourceExhaustionFallbackChain: [LlmModelName.gemini_2_5_flash],
    },
    [LlmModelName.gemini_2_0_flash_lite]: {
        id: LlmModelName.gemini_2_0_flash_lite,
        name: 'Gemini 2.0 Flash-Lite',
        addedDate: '2025-03-20',
        provider: LlmProvider.Google,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2026-02-18',
            obsoleteDate: '2026-06-01',
            successorModelFamily: LlmModelFamily.GeminiFlashLite,
        },
        intelligenceScore: 7,
        modelFamily: LlmModelFamily.GeminiFlashLite,
    },
    [LlmModelName.gemini_2_0_flash_thinking_exp_01_21]: {
        id: LlmModelName.gemini_2_0_flash_thinking_exp_01_21,
        name: 'Gemini 2.0 Flash Thinking Exp 01-21',
        addedDate: '2025-02-11',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2025-11-04',
            obsoleteDate: '2025-12-02',
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 9,
        modelFamily: LlmModelFamily.GeminiFlash,
    },
    [LlmModelName.gemini_2_5_flash]: {
        id: LlmModelName.gemini_2_5_flash,
        name: 'Gemini 2.5 Flash',
        addedDate: '2025-05-20',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-08-06',
            obsoleteDate: '2026-10-20',
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 13,
        modelFamily: LlmModelFamily.GeminiFlash,
        resourceExhaustionFallbackChain: [LlmModelName.gpt_5_4_mini],
    },
    [LlmModelName.gemini_2_5_flash_lite]: {
        id: LlmModelName.gemini_2_5_flash_lite,
        name: 'Gemini 2.5 Flash-Lite',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 9,
        modelFamily: LlmModelFamily.GeminiFlashLite,
    },
    [LlmModelName.gemini_2_5_pro_preview_03_25]: {
        id: LlmModelName.gemini_2_5_pro_preview_03_25,
        name: 'Gemini 2.5 Pro Preview 03-25',
        addedDate: '2025-05-15',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2025-11-04',
            obsoleteDate: '2025-12-02',
            successorModelFamily: LlmModelFamily.GeminiPro,
        },
        intelligenceScore: 15,
        modelFamily: LlmModelFamily.GeminiPro,
    },
    [LlmModelName.gemini_2_5_pro]: {
        id: LlmModelName.gemini_2_5_pro,
        name: 'Gemini 2.5 Pro',
        addedDate: '2025-06-06',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-08-06',
            obsoleteDate: '2026-10-20',
            successorModelFamily: LlmModelFamily.GeminiPro,
        },
        intelligenceScore: 16,
        modelFamily: LlmModelFamily.GeminiPro,
        resourceExhaustionFallbackChain: [LlmModelName.gpt_5_4],
    },
    [LlmModelName.gemini_3_pro_preview]: {
        id: LlmModelName.gemini_3_pro_preview,
        name: 'Gemini 3 Pro Preview',
        addedDate: '2025-11-19',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2026-02-26',
            obsoleteDate: '2026-03-09',
            successorModelFamily: LlmModelFamily.GeminiPro,
        },
        intelligenceScore: 28,
        modelFamily: LlmModelFamily.GeminiPro,
    },
    [LlmModelName.gemini_3_flash_preview]: {
        id: LlmModelName.gemini_3_flash_preview,
        name: 'Gemini 3 Flash Preview',
        addedDate: '2026-02-26',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-04-23',
            successorModelFamily: LlmModelFamily.GeminiFlash,
        },
        intelligenceScore: 26,
        modelFamily: LlmModelFamily.GeminiFlash,
    },
    [LlmModelName.gemini_3_1_pro_preview]: {
        id: LlmModelName.gemini_3_1_pro_preview,
        name: 'Gemini 3.1 Pro Preview',
        addedDate: '2026-02-26',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-04-23',
            successorModelFamily: LlmModelFamily.GeminiPro,
        },
        intelligenceScore: 30,
        modelFamily: LlmModelFamily.GeminiPro,
    },
    [LlmModelName.gemini_3_1_flash_lite_preview]: {
        id: LlmModelName.gemini_3_1_flash_lite_preview,
        name: 'Gemini 3.1 Flash-Lite Preview',
        addedDate: '2026-03-03',
        provider: LlmProvider.Google,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-04-23',
            successorModelFamily: LlmModelFamily.GeminiFlashLite,
        },
        intelligenceScore: 16,
        modelFamily: LlmModelFamily.GeminiFlashLite,
    },
    [LlmModelName.gpt_4_1]: {
        id: LlmModelName.gpt_4_1,
        name: 'GPT-4.1',
        addedDate: '2025-04-16',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 13,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_4_1_mini]: {
        id: LlmModelName.gpt_4_1_mini,
        name: 'GPT-4.1-mini',
        addedDate: '2025-04-16',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.GptMini },
        intelligenceScore: 10,
        modelFamily: LlmModelFamily.GptMini,
    },
    [LlmModelName.gpt_4_1_nano]: {
        id: LlmModelName.gpt_4_1_nano,
        name: 'GPT-4.1-nano',
        addedDate: '2025-04-16',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-04-22',
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.GptNano,
        },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.GptNano,
    },
    [LlmModelName.gpt_4o]: {
        id: LlmModelName.gpt_4o,
        name: 'GPT-4o',
        addedDate: '2024-12-23',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_4o_mini]: {
        id: LlmModelName.gpt_4o_mini,
        name: 'GPT-4o-mini',
        addedDate: '2024-12-23',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            obsoleteDate: '2026-08-27',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.GptMini,
        },
        intelligenceScore: 7,
        modelFamily: LlmModelFamily.GptMini,
    },
    [LlmModelName.gpt_5]: {
        id: LlmModelName.gpt_5,
        name: 'GPT-5',
        addedDate: '2025-08-07',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-06-11',
            obsoleteDate: '2026-12-11',
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 23,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_5_mini]: {
        id: LlmModelName.gpt_5_mini,
        name: 'GPT-5-mini',
        addedDate: '2025-08-07',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-06-11',
            obsoleteDate: '2026-12-11',
            successorModelFamily: LlmModelFamily.GptMini,
        },
        intelligenceScore: 17,
        modelFamily: LlmModelFamily.GptMini,
    },
    [LlmModelName.gpt_5_nano]: {
        id: LlmModelName.gpt_5_nano,
        name: 'GPT-5-nano',
        addedDate: '2025-08-07',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-06-11',
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.GptNano,
        },
        intelligenceScore: 13,
        modelFamily: LlmModelFamily.GptNano,
    },
    [LlmModelName.gpt_5_1]: {
        id: LlmModelName.gpt_5_1,
        name: 'GPT-5.1',
        addedDate: '2025-12-29',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 25,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_5_2]: {
        id: LlmModelName.gpt_5_2,
        name: 'GPT-5.2',
        addedDate: '2025-12-29',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 30,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_5_4]: {
        id: LlmModelName.gpt_5_4,
        name: 'GPT-5.4',
        addedDate: '2026-03-09',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 39,
        modelFamily: LlmModelFamily.Gpt,
    },
    [LlmModelName.gpt_5_4_mini]: {
        id: LlmModelName.gpt_5_4_mini,
        name: 'GPT-5.4-mini',
        addedDate: '2026-03-18',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.GptMini },
        intelligenceScore: 24,
        modelFamily: LlmModelFamily.GptMini,
    },
    [LlmModelName.gpt_5_4_nano]: {
        id: LlmModelName.gpt_5_4_nano,
        name: 'GPT-5.4-nano',
        addedDate: '2026-03-18',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.GptNano },
        intelligenceScore: 21,
        modelFamily: LlmModelFamily.GptNano,
    },
    [LlmModelName.gpt_5_6_sol]: {
        id: LlmModelName.gpt_5_6_sol,
        name: 'GPT-5.6 Sol',
        addedDate: '2026-10-01',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { structuredOutputs: SUPPORTED },
        lifecycle: { successorModelFamily: LlmModelFamily.Gpt },
        intelligenceScore: 47,
        modelFamily: LlmModelFamily.Gpt,
        contextLength: 1_050_000,
        maxOutputTokens: 128_000,
    },
    [LlmModelName.gpt_5_6_terra]: {
        id: LlmModelName.gpt_5_6_terra,
        name: 'GPT-5.6 Terra',
        addedDate: '2026-10-01',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { structuredOutputs: SUPPORTED },
        lifecycle: { successorModelFamily: LlmModelFamily.GptMini },
        intelligenceScore: 42,
        modelFamily: LlmModelFamily.GptMini,
        contextLength: 1_050_000,
        maxOutputTokens: 128_000,
    },
    [LlmModelName.gpt_5_6_luna]: {
        id: LlmModelName.gpt_5_6_luna,
        name: 'GPT-5.6 Luna',
        addedDate: '2026-10-01',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { structuredOutputs: SUPPORTED },
        lifecycle: { successorModelFamily: LlmModelFamily.GptNano },
        intelligenceScore: 37,
        modelFamily: LlmModelFamily.GptNano,
        contextLength: 1_050_000,
        maxOutputTokens: 128_000,
    },
    [LlmModelName.claude_opus_4_1]: {
        id: LlmModelName.claude_opus_4_1,
        name: 'Claude Opus 4.1',
        addedDate: '2025-09-30',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.ClaudeOpus,
        },
        intelligenceScore: 23,
        modelFamily: LlmModelFamily.ClaudeOpus,
        resourceExhaustionFallbackChain: [LlmModelName.claude_opus_4_6],
    },
    [LlmModelName.claude_opus_4_5]: {
        id: LlmModelName.claude_opus_4_5,
        name: 'Claude Opus 4.5',
        addedDate: '2025-12-23',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-05-28',
            obsoleteDate: '2026-11-24',
            successorModelFamily: LlmModelFamily.ClaudeOpus,
        },
        intelligenceScore: 29,
        modelFamily: LlmModelFamily.ClaudeOpus,
        resourceExhaustionFallbackChain: [LlmModelName.claude_opus_4_6],
    },
    [LlmModelName.claude_haiku_4_5]: {
        id: LlmModelName.claude_haiku_4_5,
        name: 'Claude Haiku 4.5',
        addedDate: '2025-12-23',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_AND_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.ClaudeHaiku },
        intelligenceScore: 17,
        modelFamily: LlmModelFamily.ClaudeHaiku,
        resourceExhaustionFallbackChain: [LlmModelName.claude_sonnet_4_5],
    },
    [LlmModelName.claude_sonnet_4_5]: {
        id: LlmModelName.claude_sonnet_4_5,
        name: 'Claude Sonnet 4.5',
        addedDate: '2025-09-30',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.ClaudeSonnet },
        intelligenceScore: 21,
        modelFamily: LlmModelFamily.ClaudeSonnet,
        resourceExhaustionFallbackChain: [LlmModelName.claude_sonnet_4_6],
    },
    [LlmModelName.claude_opus_4_6]: {
        id: LlmModelName.claude_opus_4_6,
        name: 'Claude Opus 4.6',
        addedDate: '2026-02-26',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.ClaudeOpus },
        intelligenceScore: 32,
        modelFamily: LlmModelFamily.ClaudeOpus,
        resourceExhaustionFallbackChain: [LlmModelName.claude_opus_4_5],
    },
    [LlmModelName.claude_sonnet_4_6]: {
        id: LlmModelName.claude_sonnet_4_6,
        name: 'Claude Sonnet 4.6',
        addedDate: '2026-02-26',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: { successorModelFamily: LlmModelFamily.ClaudeSonnet },
        intelligenceScore: 30,
        modelFamily: LlmModelFamily.ClaudeSonnet,
        resourceExhaustionFallbackChain: [LlmModelName.claude_sonnet_4_5],
    },
    [LlmModelName.claude_opus_5_5]: {
        id: LlmModelName.claude_opus_5_5,
        name: 'Claude Opus 5.5',
        addedDate: '2026-10-01',
        provider: LlmProvider.Anthropic,
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { structuredOutputs: SUPPORTED },
        lifecycle: { successorModelFamily: LlmModelFamily.ClaudeOpus },
        intelligenceScore: 58,
        modelFamily: LlmModelFamily.ClaudeOpus,
        contextLength: 1_000_000,
        maxOutputTokens: 128_000,
        resourceExhaustionFallbackChain: [LlmModelName.claude_opus_4_6],
    },
    [LlmModelName.meta_llama_3_2_11b]: {
        id: LlmModelName.meta_llama_3_2_11b,
        name: 'Meta Llama 3.2 11B',
        addedDate: '2024-12-23',
        provider: LlmProvider.Meta,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            retiredDate: '2026-01-07',
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 5,
        modelFamily: LlmModelFamily.MetaLlama,
    },
    [LlmModelName.meta_llama_3_3_70b]: {
        id: LlmModelName.meta_llama_3_3_70b,
        name: 'Meta Llama 3.3 70B',
        addedDate: '2024-12-30',
        provider: LlmProvider.Meta,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: {
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 8,
        modelFamily: LlmModelFamily.MetaLlama,
    },
    [LlmModelName.o1]: {
        id: LlmModelName.o1,
        name: 'o1',
        addedDate: '2024-12-30',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-04-22',
            obsoleteDate: '2026-08-27',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.O,
        },
        intelligenceScore: 15,
        modelFamily: LlmModelFamily.O,
    },
    [LlmModelName.o3]: {
        id: LlmModelName.o3,
        name: 'o3',
        addedDate: '2025-06-03',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-06-11',
            obsoleteDate: '2026-06-26',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.Gpt,
        },
        intelligenceScore: 20,
        modelFamily: LlmModelFamily.O,
    },
    [LlmModelName.o1_mini]: {
        id: LlmModelName.o1_mini,
        name: 'o1-mini',
        addedDate: '2024-12-23',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_OR_TOP_P },
        lifecycle: {
            retiredDate: '2025-04-28',
            obsoleteDate: '2025-10-27',
            successorModelFamily: LlmModelFamily.OMini,
        },
        intelligenceScore: 10,
        modelFamily: LlmModelFamily.OMini,
    },
    [LlmModelName.o3_mini]: {
        id: LlmModelName.o3_mini,
        name: 'o3-mini',
        addedDate: '2025-02-03',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-04-22',
            obsoleteDate: '2026-08-27',
            forceObsolete: true,
            successorModelFamily: LlmModelFamily.OMini,
        },
        intelligenceScore: 12,
        modelFamily: LlmModelFamily.OMini,
    },
    [LlmModelName.o4_mini]: {
        id: LlmModelName.o4_mini,
        name: 'o4-mini',
        addedDate: '2025-06-03',
        provider: LlmProvider.OpenAI,
        inputModalities: TEXT_ONLY,
        outputModalities: TEXT_ONLY,
        supportedParameters: {
            ...TEMPERATURE_OR_TOP_P,
            structuredOutputs: SUPPORTED,
        },
        lifecycle: {
            retiredDate: '2026-04-22',
            obsoleteDate: '2026-10-23',
            successorModelFamily: LlmModelFamily.OMini,
        },
        intelligenceScore: 17,
        modelFamily: LlmModelFamily.OMini,
    },
    [LlmModelName.notable_default]: {
        id: LlmModelName.notable_default,
        name: 'Auto',
        addedDate: '2025-04-21',
        inputModalities: TEXT_AND_FILE,
        outputModalities: TEXT_ONLY,
        supportedParameters: { ...TEMPERATURE_AND_TOP_P },
        lifecycle: { successorModelFamily: LlmModelFamily.NotableDefault },
        modelFamily: LlmModelFamily.NotableDefault,
        resourceExhaustionFallbackChain: [
            LlmModelName.gemini_2_5_flash,
            LlmModelName.gpt_5_4_mini,
            LlmModelName.gpt_5_4_nano,
        ],
    },
};
/**
 * Ordered execution candidates per lifecycle successor family; the first one
 * that is neither upcoming nor obsolete runs. Stage a scheduled head by
 * prepending it.
 */
export const LLM_MODEL_FAMILY_CANDIDATES = {
    [LlmModelFamily.GeminiPro]: [LlmModelName.gpt_5_6_sol, LlmModelName.gpt_5_4],
    [LlmModelFamily.GeminiFlash]: [LlmModelName.gpt_5_6_terra, LlmModelName.gpt_5_4_mini],
    [LlmModelFamily.GeminiFlashLite]: [LlmModelName.gpt_5_6_luna, LlmModelName.gpt_5_4_nano],
    [LlmModelFamily.ClaudeOpus]: [LlmModelName.claude_opus_5_5, LlmModelName.claude_opus_4_6],
    [LlmModelFamily.ClaudeSonnet]: [LlmModelName.claude_sonnet_4_6],
    [LlmModelFamily.ClaudeHaiku]: [LlmModelName.claude_haiku_4_5],
    [LlmModelFamily.Gpt]: [LlmModelName.gpt_5_6_sol, LlmModelName.gpt_5_4],
    [LlmModelFamily.GptMini]: [LlmModelName.gpt_5_6_terra, LlmModelName.gpt_5_4_mini],
    [LlmModelFamily.GptNano]: [LlmModelName.gpt_5_6_luna, LlmModelName.gpt_5_4_nano],
    [LlmModelFamily.O]: [LlmModelName.gpt_5_6_sol, LlmModelName.gpt_5_4],
    [LlmModelFamily.OMini]: [LlmModelName.gpt_5_6_terra, LlmModelName.gpt_5_4_mini],
    [LlmModelFamily.AmazonNova]: [LlmModelName.amazon_nova_pro],
    [LlmModelFamily.DeepseekReasoning]: [LlmModelName.deepseek_r1],
    [LlmModelFamily.DeepseekChat]: [LlmModelName.deepseek_v3],
    [LlmModelFamily.MetaLlama]: [LlmModelName.meta_llama_3_3_70b],
    [LlmModelFamily.NotableDefault]: [LlmModelName.notable_default],
};
/** Applies registry defaults: the parameter-support matrix and null lifecycle dates. */
function resolveModel(input) {
    return {
        ...input,
        supportedParameters: { ...DEFAULT_SUPPORTED_PARAMETERS, ...input.supportedParameters },
        lifecycle: { retiredDate: null, obsoleteDate: null, ...input.lifecycle },
    };
}
/** The fully-resolved model registry (defaults applied, dates merged in). */
export const LLM_MODELS = Object.fromEntries(Object.entries(RAW_LLM_MODELS).map(([id, model]) => [id, resolveModel(model)]));
/** Returns the model for a stable registry identifier, if it exists. */
export function getLlmModel(modelName) {
    if (!Object.hasOwn(LLM_MODELS, modelName)) {
        return undefined;
    }
    return LLM_MODELS[modelName];
}
//# sourceMappingURL=models.js.map